import React, { createContext, type ReactNode, useContext, useMemo } from 'react';
import type { Product } from '../../types';

interface UnifiedAnalyticsConfig {
	crossAppInteractionSessionsEnabled?: boolean;
	productSwitcher?: Product | string;
	unifiedInstrumentationEnabled?: boolean;
}

interface UnifiedAnalyticsContextProviderProps {
	children: ReactNode;
	crossAppInteractionSessionsEnabled?: boolean;
	productSwitcher?: Product | string;
	unifiedInstrumentationEnabled?: boolean;
}

const UnifiedAnalyticsContext = createContext<UnifiedAnalyticsConfig | undefined>(undefined);

export const UnifiedAnalyticsContextProvider = ({
	unifiedInstrumentationEnabled = false,
	crossAppInteractionSessionsEnabled = false,
	productSwitcher = 'appSwitcher',
	children,
}: UnifiedAnalyticsContextProviderProps) => {
	const contextValue = useMemo<UnifiedAnalyticsConfig>(
		() => ({
			unifiedInstrumentationEnabled,
			crossAppInteractionSessionsEnabled,
			productSwitcher,
		}),
		[productSwitcher, unifiedInstrumentationEnabled, crossAppInteractionSessionsEnabled],
	);

	return (
		<UnifiedAnalyticsContext.Provider value={contextValue}>
			{children}
		</UnifiedAnalyticsContext.Provider>
	);
};

export const useUnifiedAnalyticsContext = (): UnifiedAnalyticsConfig => {
	const context = useContext(UnifiedAnalyticsContext);

	return (
		context ?? {
			unifiedInstrumentationEnabled: false,
			crossAppInteractionSessionsEnabled: false,
			productSwitcher: 'appSwitcher',
		}
	);
};
